import type { Locale, TranslationKey } from "./i18n";

type NaturalnessCatalog = Partial<
  Record<Locale, Partial<Record<TranslationKey, string>>>
>;

/**
 * Replaces catalog entries that were technically present but still copied the
 * English UI verbatim. Product names, indicator acronyms and numeric formats
 * are intentionally handled by the explicit allowlist in the localization
 * contract test instead of being duplicated here.
 */
const completeNaturalnessTranslations = {
  vi: {
    appSubtitle: "Trình kiểm thử chiến lược",
    email: "Địa chỉ email",
    strategyBreakout: "Phá vỡ",
    strategyPullback: "Hồi giá",
    aiParser: "Bộ phân tích AI",
    strategyPinBar: "Nến pin bar",
    strategyInsideBar: "Nến inside bar",
  },
  hi: {
    appSubtitle: "रणनीति परीक्षक",
    cloudHistory: "क्लाउड इतिहास",
    bots: "बॉट",
    cryptoLab: "ट्रेडिंग रणनीति मंच",
    delete: "हटाएँ",
    endDate: "समाप्ति तिथि",
    entry: "प्रवेश",
    exit: "निकास",
    firebaseNotSet: "खाता सेवा उपलब्ध नहीं है",
    manualPrompt: "मैन्युअल प्रॉम्प्ट",
    maxRange: "अधिकतम अवधि",
    mode: "मोड",
    netProfit: "शुद्ध लाभ",
    promptPreview: "नियम-पार्सर प्रॉम्प्ट",
    quotaHistory: "इतिहास",
    ready: "तैयार",
    returnMetric: "प्रतिफल",
    savedSetups: "सहेजे गए सेटअप",
    settings: "सेटिंग्स",
    signedIn: "साइन इन है",
    startDate: "आरंभ तिथि",
    strategyBreakout: "ब्रेकआउट",
    strategyMACross: "MA क्रॉस",
    strategyPullback: "पुलबैक",
    tick: "अपडेट",
    refresh: "रीफ़्रेश करें",
    exitMode: "निकास मोड",
    signalAndRisk: "सिग्नल + जोखिम",
    aiParser: "AI पार्सर",
    strategyEngulfing: "एनगल्फ़िंग",
    strategyPinBar: "पिन बार",
    strategyInsideBar: "इनसाइड बार",
    strategyTrendPlus: "ट्रेंड+",
    strategyMeanPlus: "मीन रिवर्ज़न+",
    realtimeBotDeleted: "रीयलटाइम बॉट हटा दिया गया।",
    chartIndicators: "चार्ट संकेतक",
    guestPlan: "अतिथि",
  },
  ur: {
    appSubtitle: "حکمت عملی آزمانے کا آلہ",
    cloudHistory: "کلاؤڈ ہسٹری",
    bots: "بوٹس",
    cryptoLab: "ٹریڈنگ حکمت عملی کا پلیٹ فارم",
    delete: "حذف کریں",
    endDate: "اختتامی تاریخ",
    entry: "داخلہ",
    exit: "اخراج",
    firebaseNotSet: "اکاؤنٹ سروس دستیاب نہیں",
    manualPrompt: "دستی پرامپٹ",
    maxRange: "زیادہ سے زیادہ مدت",
    mode: "موڈ",
    netProfit: "خالص منافع",
    promptPreview: "رول پارسر پرامپٹ",
    quotaHistory: "تاریخ",
    ready: "تیار",
    returnMetric: "منافع",
    savedSetups: "محفوظ سیٹ اپس",
    settings: "ترتیبات",
    signedIn: "سائن اِن ہے",
    startDate: "آغاز کی تاریخ",
    strategyBreakout: "بریک آؤٹ",
    strategyMACross: "MA کراس",
    strategyPullback: "پل بیک",
    tick: "اپ ڈیٹ",
    refresh: "تازہ کریں",
    exitMode: "اخراج کا طریقہ",
    signalAndRisk: "سگنل + خطرہ",
    aiParser: "AI پارسر",
    strategyEngulfing: "انگلفنگ",
    strategyPinBar: "پن بار",
    strategyInsideBar: "ان سائیڈ بار",
    strategyTrendPlus: "ٹرینڈ+",
    strategyMeanPlus: "اوسط واپسی+",
    chartIndicators: "چارٹ انڈیکیٹرز",
  },
  th: {
    drawdown: "การขาดทุนสะสมสูงสุด",
    slippage: "สลิปเพจ",
    strategyBreakout: "เบรกเอาต์",
    strategyMACross: "จุดตัด MA",
    strategyPullback: "จังหวะย่อตัว",
    tick: "อัปเดต",
    signalAndRisk: "สัญญาณ + ความเสี่ยง",
    aiParser: "ตัวแปลกฎด้วย AI",
    chartVolume: "ปริมาณซื้อขาย",
    strategyEngulfing: "แท่งเทียนกลืนกิน",
    strategyPinBar: "พินบาร์",
    strategyInsideBar: "อินไซด์บาร์",
    strategyTrendPlus: "แนวโน้ม+",
    strategyMeanPlus: "กลับสู่ค่าเฉลี่ย+",
    guestPlan: "ผู้เยี่ยมชม",
  },
  bn: {
    appSubtitle: "কৌশল পরীক্ষক",
    cloudHistory: "ক্লাউড ইতিহাস",
    bots: "বট",
    cryptoLab: "ট্রেডিং কৌশল প্ল্যাটফর্ম",
    delete: "মুছুন",
    endDate: "শেষ তারিখ",
    entry: "প্রবেশ",
    exit: "প্রস্থান",
    firebaseNotSet: "অ্যাকাউন্ট সেবা অনুপলভ্য",
    manualPrompt: "ম্যানুয়াল প্রম্পট",
    maxRange: "সর্বোচ্চ পরিসর",
    mode: "মোড",
    netProfit: "নিট মুনাফা",
    promptPreview: "নিয়ম-পার্সার প্রম্পট",
    quotaHistory: "ইতিহাস",
    ready: "প্রস্তুত",
    returnMetric: "রিটার্ন",
    savedSetups: "সংরক্ষিত সেটআপ",
    settings: "সেটিংস",
    signedIn: "সাইন ইন করা আছে",
    startDate: "শুরুর তারিখ",
    strategyBreakout: "ব্রেকআউট",
    strategyMACross: "MA ক্রস",
    strategyPullback: "পুলব্যাক",
    tick: "আপডেট",
    refresh: "রিফ্রেশ করুন",
    exitMode: "প্রস্থান মোড",
    signalAndRisk: "সিগন্যাল + ঝুঁকি",
    aiParser: "AI পার্সার",
    chartVolume: "লেনদেনের পরিমাণ",
    strategyEngulfing: "এনগালফিং",
    strategyPinBar: "পিন বার",
    strategyInsideBar: "ইনসাইড বার",
    strategyTrendPlus: "ট্রেন্ড+",
    strategyMeanPlus: "গড় প্রত্যাবর্তন+",
    chartIndicators: "চার্ট ইন্ডিকেটর",
    guestPlan: "অতিথি",
  },
  id: {
    appSubtitle: "Penguji strategi",
    backtest: "Uji historis",
    drawdown: "Penurunan maksimum",
    entry: "Masuk",
    exit: "Keluar",
    mode: "Cara pengujian",
    email: "Alamat email",
    returnMetric: "Imbal hasil",
    slippage: "Selisih harga",
    stopLoss: "Batas rugi",
    strategyBreakout: "Penembusan",
    strategyMACross: "Persilangan MA",
    strategyPullback: "Koreksi harga",
    takeProfit: "Ambil untung",
    timeframe: "Kerangka waktu",
    tick: "Perbarui",
    refresh: "Muat ulang",
    improve: "Perbaiki",
    signalAndRisk: "Sinyal + risiko",
    aiParser: "Pengurai AI",
    chartVolume: "Volume transaksi",
    winRate: "Tingkat kemenangan",
    strategyEngulfing: "Pola engulfing",
    strategyPinBar: "Pola pin bar",
    strategyInsideBar: "Pola inside bar",
    strategyTrendPlus: "Tren+",
    strategyMeanPlus: "Reversi rata-rata+",
    guestPlan: "Tamu",
  },
  fil: {
    account: "Akawnt",
    aiCredits: "Mga credit ng AI",
    appSubtitle: "Tagasubok ng estratehiya",
    backtest: "Pagsusuring backtest",
    backtests: "Mga backtest",
    capital: "Puhunan",
    cloudHistory: "Kasaysayan sa cloud",
    bots: "Mga bot",
    cryptoLab: "Plataporma ng estratehiya sa pangangalakal",
    drawdown: "Pinakamalaking pagbaba",
    entry: "Pagpasok",
    exit: "Paglabas",
    firebaseNotSet: "Hindi available ang serbisyo ng akawnt",
    guestMode: "Mode ng bisita",
    home: "Pangunahing pahina",
    market: "Merkado",
    manualPrompt: "Manu-manong prompt",
    maxRange: "Pinakamahabang saklaw",
    mode: "Pamamaraan",
    email: "Email address",
    password: "Lihim na password",
    netProfit: "Netong tubo",
    position: "Posisyon",
    privacy: "Pagkapribado",
    privacyThirdTitle: "Mga serbisyo ng ibang provider",
    privacyRiskTitle: "Panganib sa pangangalakal",
    promptPreview: "Preview ng tagabasa ng mga tuntunin",
    quotaHistory: "Kasaysayan",
    ready: "Handa",
    returnMetric: "Kita",
    savedSetups: "mga naka-save na setup",
    settings: "Mga setting",
    setup: "Pagkakaayos",
    signedIn: "Naka-sign in",
    slippage: "Pagdulas ng presyo",
    sample: "Sampol",
    stopLoss: "Hangganan ng lugi",
    strategy: "Estratehiya",
    buyingPower: "Kakayahang bumili",
    strategyBreakout: "Pagbasag ng antas",
    strategyMACross: "Pagkrus ng MA",
    strategyPullback: "Pag-atras ng presyo",
    takeProfit: "Kunin ang tubo",
    timeframe: "Saklaw ng oras",
    tick: "I-update",
    refresh: "I-refresh",
    trades: "Mga trade",
    improvedPrompt: "Pinahusay na prompt",
    side: "Direksiyon",
    aiReview: "Pagbasa ng mga tuntunin gamit ang AI",
    buy: "Bumili",
    sell: "Magbenta",
    fees: "Mga bayarin",
    exitMode: "Paraan ng paglabas",
    tpSlOnly: "TP/SL lamang",
    signalAndRisk: "Signal + panganib",
    signalOnly: "Signal lamang",
    aiParser: "Tagasuri ng AI",
    aiParserHint: "Gumagamit ng AI credits ang manu-manong mode kapag naka-login.",
    chartVolume: "Dami ng kalakalan",
    chartTrades: "Mga trade sa chart",
    winRate: "Bahagdan ng panalo",
    strategyEngulfing: "Engulfing na kandila",
    strategyPinBar: "Kandilang pin bar",
    strategyInsideBar: "Kandilang inside bar",
    strategyTrendPlus: "Pinahusay na trend",
    strategyMeanPlus: "Pagbalik sa average+",
    chartIndicators: "Mga indicator sa chart",
    feature: "Kakayahan",
    guestPlan: "Bisita",
    quotaSavedStrategies: "Mga naka-save na estratehiya",
    quotaPaperBots: "Mga bot ng simuladong signal",
  },
  "es-419": {
    appSubtitle: "Probador de estrategias",
    backtest: "Prueba histórica",
    backtests: "Pruebas históricas",
    capital: "Capital inicial",
    bots: "Bots de señales",
    drawdown: "Caída máxima",
    email: "Correo electrónico",
    stopLoss: "Límite de pérdida",
    strategyBreakout: "Ruptura",
    strategyPullback: "Retroceso",
    takeProfit: "Objetivo de ganancia",
    tick: "Actualizar",
    long: "Largo",
    short: "Corto",
    aiParser: "Analizador con IA",
    strategyPinBar: "Vela pin bar",
    strategyInsideBar: "Vela interior",
    guestPlan: "Invitado",
    quotaBacktests: "Pruebas históricas",
  },
  "pt-BR": {
    appSubtitle: "Testador de estratégias",
    backtest: "Teste histórico",
    backtests: "Testes históricos",
    capital: "Capital inicial",
    bots: "Bots de sinais",
    drawdown: "Queda máxima",
    email: "E-mail",
    slippage: "Deslizamento",
    stopLoss: "Limite de perda",
    strategyBreakout: "Rompimento",
    strategyPullback: "Retração",
    takeProfit: "Alvo de lucro",
    tick: "Atualizar",
    trades: "Operações",
    long: "Comprado",
    short: "Vendido",
    aiParser: "Analisador com IA",
    chartVolume: "Volume negociado",
    chartTrades: "Operações no gráfico",
    strategyPinBar: "Vela pin bar",
    strategyInsideBar: "Vela interna",
    guestPlan: "Visitante",
    quotaBacktests: "Testes históricos",
  },
  ja: {
    strategyBreakout: "ブレイクアウト",
    strategyPullback: "押し目",
    tick: "更新",
    aiParser: "AI解析",
    guestPlan: "ゲスト",
  },
  ko: {
    appSubtitle: "전략 테스터",
    strategyBreakout: "돌파",
    strategyPullback: "눌림목",
    tick: "업데이트",
    aiParser: "AI 분석기",
    guestPlan: "비회원",
  },
  ru: {
    appSubtitle: "Тестер стратегий",
    email: "Электронная почта",
    strategyBreakout: "Пробой",
    strategyMACross: "Пересечение MA",
    strategyPullback: "Откат",
    tick: "Обновить",
    aiParser: "ИИ-анализатор",
    winRate: "Доля прибыльных сделок",
    guestPlan: "Гость",
  },
  tr: {
    appSubtitle: "Strateji test aracı",
    backtest: "Geçmiş testi",
    cloudHistory: "Bulut geçmişi",
    cryptoLab: "Alım satım stratejisi platformu",
    drawdown: "Maksimum düşüş",
    entry: "Giriş",
    exit: "Çıkış",
    manualPrompt: "Manuel komut",
    email: "E-posta",
    netProfit: "Net kâr",
    promptPreview: "Kural ayrıştırıcı komutu",
    returnMetric: "Getiri",
    savedSetups: "Kayıtlı kurulumlar",
    stopLoss: "Zarar durdur",
    strategyBreakout: "Kırılım",
    strategyMACross: "MA kesişimi",
    strategyPullback: "Geri çekilme",
    takeProfit: "Kâr al",
    tick: "Güncelle",
    long: "Uzun",
    short: "Kısa",
    aiParser: "AI ayrıştırıcı",
    strategyEngulfing: "Yutan mum",
    strategyPinBar: "Pin bar mumu",
    strategyInsideBar: "İç bar",
    strategyTrendPlus: "Gelişmiş trend",
    strategyMeanPlus: "Ortalamaya dönüş+",
    chartIndicators: "Grafik göstergeleri",
    guestPlan: "Misafir",
  },
  uk: {
    appSubtitle: "Тестер стратегій",
    cloudHistory: "Хмарна історія",
    manualPrompt: "Ручний промпт",
    email: "Електронна пошта",
    promptPreview: "Промпт аналізатора правил",
    savedSetups: "Збережені налаштування",
    strategyBreakout: "Пробій",
    strategyMACross: "Перетин MA",
    strategyPullback: "Відкат",
    tick: "Оновити",
    aiParser: "AI-аналізатор",
    winRate: "Частка прибуткових угод",
    guestPlan: "Гість",
  },
} satisfies NaturalnessCatalog;

export const naturalnessTranslations: NaturalnessCatalog =
  completeNaturalnessTranslations;
